import { Box, Button, Divider, IconButton, Modal, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { ACHForm } from '~/modules/payment/components';
import { X as XIcon } from 'react-feather';
import { useCreateACHPaymentV2Mutation, useGetPaymentMethodV2Query } from '~/services/member';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { LeavingWithoutSavingPrompt } from '~/modules/@shared/components';

import { useCallback, useState } from 'react';
import { PaymentErrorModal, PaymentProcessingModal, PaymentSuccessModal } from '~/modules/payment/components/modals';
import usePaymentErrorModalStore from '~/modules/payment/components/modals/PaymentErrorModal/hooks/usePaymentErrorModalStore';
import { bankAccountNumberSchema } from '../../../../../MembershipsSection/components/PaymentConfirmationModal/helpers/paymentConfirmationModalSchema';
import { CreateACHPaymentPayload } from '~/services/types';

const achPaymentSchema = z.object({
  account_type: z.string(),
  routing_number: z.string().regex(/^\d{9}$/, 'The routing number must be exactly 9 digits'),
  bank_account_number: bankAccountNumberSchema,
});

type AchPayment = z.infer<typeof achPaymentSchema>;

type Props = {
  memberId?: number;
  locationId: number;
  handleModalClose: () => void;
  addAchOpen: boolean;
};

export default function AddAchModal({
  memberId,
  locationId,
  handleModalClose: handleExternalModalClose,
  addAchOpen,
}: Props) {
  const methods = useForm<AchPayment>({
    resolver: zodResolver(achPaymentSchema),
    defaultValues: {
      account_type: 'checking',
      routing_number: undefined,
      bank_account_number: undefined,
    },
  });
  const { handleSubmit, trigger, reset } = methods;

  const [processingOpen, setProcessingOpen] = useState(false);
  const [confirmedOpen, setConfirmedOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);

  const [pmId, setPmId] = useState(0);

  const setErrorMessage = usePaymentErrorModalStore((state: any) => state.setMsg);

  const handleModalClose = () => {
    reset();
    setConfirmedOpen(false);
    setProcessingOpen(false);
    setErrorOpen(false);
    handleExternalModalClose();
  };
  const handleModalLocalClose = () => {
    reset();
    setErrorOpen(false);
  };

  const { mutateAsync: createACHPayment } = useCreateACHPaymentV2Mutation({ locationId });
  const { refetch: refetchPaymentMethod } = useGetPaymentMethodV2Query({
    locationId,
    memberId,
    paymentMethodId: pmId,
  });

  if (!memberId) return null;

  const tryToRefreshPaymentMethod = useCallback(
    (pmId: number) => {
      return new Promise((resolve, reject) => {
        let tryCount = 0;
        const intervalId = setInterval(() => {
          tryCount++;
          if (pmId && refetchPaymentMethod) {
            const rawResp = refetchPaymentMethod();
            rawResp
              .then((raw: any) => {
                const response = raw?.data ?? { payrix_onboarding_status: -1 };
                if (response.payrix_onboarding_status === 3) {
                  clearInterval(intervalId);
                  resolve(response);
                } else if (response.payrix_onboarding_status !== 98) {
                  console.error('rejected, incorrect payrix_onboarding_status', { response });
                  clearInterval(intervalId);
                  reject({ message: response.payrix_onboarding_error ?? 'Unknown error' });
                }
              })
              .catch((e: any) => {
                console.error('tryToRefreshPaymentMethod catched error', { e });
              });
          }
          if (tryCount === 10) {
            console.error('Max attempts reached', { tryCount });
            clearInterval(intervalId);
            reject({ message: 'No response' });
          }
        }, 1000);
      });
    },
    [refetchPaymentMethod],
  );

  const createACHRequest = (data: AchPayment, location_id: number, member_id: number): CreateACHPaymentPayload => {
    return {
      account_ach: data.bank_account_number,
      expiration: '', //response.data[0].expiration,
      default: true,
      inactive: false,
      last_4_digits_card: '',
      location_id,
      member_id,
      method: data.account_type === 'checking' ? 8 : data.account_type === 'savings' ? 9 : 0,
      payrix_token_id: '',
      routing_ach: data.routing_number,
      token: '',
    };
  };

  const handleSavePayment = async (data: AchPayment) => {
    setProcessingOpen(true);
    setErrorMessage('');
    let paymentMethodId = 0;

    try {
      const paymentRequest = createACHRequest(data, locationId, memberId);
      const createACHPaymentResponse = await createACHPayment(paymentRequest);

      if (createACHPaymentResponse?.status === 'CREATED') {
        paymentMethodId = createACHPaymentResponse.data.id;
        setPmId(paymentMethodId);
      } else {
        console.error({ paymentRequest, createACHPaymentResponse });
        throw new Error('Error on payment method: ' + (createACHPaymentResponse?.message ?? 'Unknown error'));
      }

      const preAchResponse = createACHPaymentResponse?.data ?? { payrix_onboarding_status: -1 };

      if (preAchResponse.payrix_onboarding_status === 98) {
        const newAchResponse: any = await tryToRefreshPaymentMethod(paymentMethodId);
        if (newAchResponse?.payrix_onboarding_status === 3) {
          setProcessingOpen(false);
          setConfirmedOpen(true);
        } else {
          throw new Error(
            'Error on payment method: ' +
              (newAchResponse?.data?.payrix_onboarding_error ?? newAchResponse?.data?.message ?? 'Unknown error'),
          );
        }
      } else {
        throw new Error(
          'Error on payment method: ' +
            (createACHPaymentResponse?.data?.payrix_onboarding_error ??
              createACHPaymentResponse?.message ??
              'Unknown error'),
        );
      }
    } catch (e: any) {
      console.error({ e });
      setErrorMessage(e.message);
      setProcessingOpen(false);
      setErrorOpen(true);
    } finally {
      reset();
    }
  };

  return addAchOpen ? (
    <>
      <FormProvider {...methods}>
        <LeavingWithoutSavingPrompt />
        <Modal
          open={true}
          onClose={handleModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box
            sx={{
              borderRadius: '10px',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 600,
              bgcolor: 'rgb(246, 247, 251)',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
              <Typography variant="h2">Add Bank Account</Typography>
              <IconButton onClick={handleModalClose}>
                <XIcon size={16} />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ px: 2 }}>{<ACHForm />}</Box>
            <Divider />
            <Box sx={{ pt: 3, pb: 3, pr: 3, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Button variant="outlined" size="large" onClick={handleModalClose} sx={{ marginRight: '1em' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  void trigger();
                  void handleSubmit(handleSavePayment)();
                }}
                sx={{ width: '200px' }}>
                Save Payment
              </Button>
            </Box>
          </Box>
        </Modal>
      </FormProvider>
      <PaymentProcessingModal {...{ processingOpen }} />
      <PaymentSuccessModal {...{ confirmedOpen, handleModalClose }} />
      <PaymentErrorModal {...{ errorOpen, handleModalClose, handleModalLocalClose }} />
    </>
  ) : null;
}
